import { useMemo, useState } from "react";

import { Box, Checkbox, CircularProgress, Paper, Typography } from "@material-ui/core";

import themeColorsDesingSystem from "assets/theme/ds-colors";

import RepresentativeCard from "./representative-card";
import ListPagination from "./list-pagination";
import BulkSelectionModal from "./bulk-selection-modal";
import { REPRESENTATIVE_PAGE_SIZE } from "../utils/representative-helpers";

export default function RepresentativeSelectionList({
  isLoading,
  items = [],
  totalItems = 0,
  page = 0,
  totalPages = 0,
  pageSize = REPRESENTATIVE_PAGE_SIZE,
  onPageChange,
  selectedIds,
  onToggleItem,
  onSelectVisible,
  onDeselectVisible,
  onSelectAllResults,
  onDeselectAll,
  isItemSelectable = () => true,
  emptyMessage = "Nenhum registro encontrado com os filtros aplicados.",
  selectionFooter,
  selectAllDialogTitle = "Selecionar registros",
  selectAllEntityLabel = "registros",
}) {
  const [openSelectAllDialog, setOpenSelectAllDialog] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  const selectableItems = useMemo(
    () => items.filter(isItemSelectable),
    [items, isItemSelectable],
  );
  const visibleIds = useMemo(
    () => selectableItems.map((item) => item.__selectionId),
    [selectableItems],
  );

  const pageSelectedCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected =
    selectableItems.length > 0 && pageSelectedCount === selectableItems.length;
  const someVisibleSelected = pageSelectedCount > 0 && !allVisibleSelected;

  const selectAllResults = async () => {
    if (!onSelectAllResults) {
      onSelectVisible?.(visibleIds);
      return;
    }
    setSelectAllLoading(true);
    try {
      await onSelectAllResults();
    } finally {
      setSelectAllLoading(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      style={{
        marginTop: 24,
        borderColor: themeColorsDesingSystem.stone.main,
        overflow: "hidden",
      }}
    >
      <Box
        style={{
          minHeight: 160,
          maxHeight: "min(420px, 45vh)",
          overflowY: "auto",
          padding: "4px 8px",
          backgroundColor: themeColorsDesingSystem.background.main,
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Box
              display="flex"
              alignItems="center"
              py={1}
              px={1}
              style={{
                borderBottom: `1px solid ${themeColorsDesingSystem.stone.light}`,
              }}
            >
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected}
                onChange={() => setOpenSelectAllDialog(true)}
                disabled={items.length === 0}
                color="primary"
                size="small"
              />
              <Typography
                variant="subtitle2"
                style={{
                  margin: 0,
                  fontWeight: 700,
                  color: themeColorsDesingSystem.primary.main,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Todos
              </Typography>
            </Box>

            {items.length === 0 ? (
              <Box py={4} px={2} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  {emptyMessage}
                </Typography>
              </Box>
            ) : (
              items.map((item) => (
                <RepresentativeCard
                  key={item.__selectionId}
                  item={item}
                  checked={selectedIds.has(item.__selectionId)}
                  onChange={() => onToggleItem(item)}
                  disabled={isLoading}
                />
              ))
            )}
          </>
        )}
      </Box>

      <Box px={2} pb={1}>
        <ListPagination
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      </Box>

      {selectionFooter}

      <BulkSelectionModal
        open={openSelectAllDialog}
        onClose={() => (selectAllLoading ? undefined : setOpenSelectAllDialog(false))}
        title={selectAllDialogTitle}
        entityLabel={selectAllEntityLabel}
        pageCount={selectableItems.length}
        pageSelectedCount={pageSelectedCount}
        totalCount={totalItems}
        totalSelectedCount={selectedIds.size}
        loading={selectAllLoading}
        onSelectPage={() => onSelectVisible?.(visibleIds)}
        onSelectAll={selectAllResults}
        onDeselectPage={() => onDeselectVisible?.(visibleIds)}
        onDeselectAll={onDeselectAll}
      />
    </Paper>
  );
}
